import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Heart, DollarSign, CreditCard, Landmark, QrCode, BarChart2, ThumbsUp, ThumbsDown, Users } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { statisticsApi, communityApi } from '../services/apiService';

// Weather statistics for display when API metrics are not available yet
const DEFAULT_WEATHER_TYPES = 6; // Sunny, Cloudy, Partly Cloudy, Rainy, Stormy, Foggy

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const countUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

// Styled components
const CommunitySection = styled.section`
  padding: 6rem 2rem;
  background-color: ${props => props.theme.colors.background};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 30% 70%, rgba(255, 99, 132, 0.08) 0%, transparent 70%);
    z-index: 1;
  }
`;

const SectionInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  animation: ${fadeIn} 0.8s ease forwards;
  opacity: 0;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textLight};
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 4rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: ${props => props.theme.shadows.small};
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  opacity: 0;
  animation: ${fadeIn} 0.5s ease forwards;
  animation-delay: ${props => props.delay || '0s'};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.shadows.medium};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin: 0 auto;
    max-width: 300px;
  }
`;

const StatIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: ${props => props.color || props.theme.colors.primary};
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin-bottom: 0.5rem;
  opacity: 0;
  animation: ${countUp} 0.8s ease forwards;
  animation-delay: ${props => `calc(${props.delay} + 0.3s)` || '0.3s'};
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: ${props => props.theme.colors.textLight};
`;

const CommunityGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 4rem;
  }
`;

const CommunityColumn = styled.div`
  opacity: 0;
  animation: ${fadeIn} 0.8s ease forwards;
  animation-delay: ${props => props.delay || '0s'};
`;

const ContributorsContainer = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: ${props => props.theme.shadows.medium};
`;

const ContributorsTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.75rem;
    color: ${props => props.theme.colors.primary};
  }
`;

const ContributorsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ContributorCard = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  border-radius: 8px;
  background-color: ${props => props.theme.colors.backgroundAlt};
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(5px);
  }
`;

const ContributorAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 1rem;
  flex-shrink: 0;
  background-color: ${props => props.theme.colors.primaryLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
`;

const ContributorInfo = styled.div`
  flex: 1;
`;

const ContributorName = styled.div`
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: ${props => props.theme.colors.text};
`;

const ContributorStats = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textLight};
  display: flex;
  align-items: center;
  
  span {
    display: flex;
    align-items: center;
    margin-right: 1rem;
    
    svg {
      margin-right: 0.25rem;
      font-size: 0.8rem;
    }
  }
`;

const ContributorBadge = styled.div`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  background-color: ${props => props.color || props.theme.colors.primaryLight};
  color: ${props => props.$textColor || props.theme.colors.primary};
  margin-left: auto;
`;

const VotingContainer = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: ${props => props.theme.shadows.medium};
`;

const VotingTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.75rem;
    color: ${props => props.theme.colors.primary};
  }
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FeatureItem = styled.div`
  position: relative;
`;

const FeatureHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const FeatureTitle = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const FeatureStatus = styled.div`
  font-size: 0.8rem;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  background-color: ${props => props.color || props.theme.colors.primaryLight};
  color: ${props => props.$textColor || props.theme.colors.primary};
`;

const ProgressBar = styled.div`
  height: 10px;
  background-color: ${props => props.theme.colors.backgroundAlt};
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 0.5rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.percentage}%;
  background-color: ${props => props.color || props.theme.colors.primary};
  border-radius: 5px;
  transition: width 1s ease-in-out;
`;

const VoteButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const VoteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  background-color: ${props => props.active ? props.color : props.theme.colors.backgroundAlt};
  color: ${props => props.active ? 'white' : props.theme.colors.textLight};
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
    background-color: ${props => props.color};
    color: white;
  }
  
  &:focus {
    outline: none;
  }
`;

const VoteCount = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textLight};
  display: flex;
  justify-content: space-between;
`;

const TestimonialsContainer = styled.div`
  margin-top: 4rem;
  position: relative;
  opacity: 0;
  animation: ${fadeIn} 0.8s ease forwards;
  animation-delay: 0.6s;
`;

const TestimonialsTitle = styled.h3`
  font-size: 1.75rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.text};
`;

const TestimonialsSlider = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  gap: 2rem;
  padding: 1rem 0;
  
  /* Hide scrollbar */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const TestimonialCard = styled.div`
  flex: 0 0 360px;
  scroll-snap-align: start;
  padding: 2rem;
  border-radius: 12px;
  background-color: white;
  box-shadow: ${props => props.theme.shadows.medium};
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex: 0 0 300px;
  }
`;

const TestimonialContent = styled.div`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.text};
  line-height: 1.6;
  margin-bottom: 1.5rem;
  position: relative;
  
  &::before {
    content: '"';
    font-size: 3rem;
    color: ${props => props.theme.colors.primaryLight};
    position: absolute;
    top: -1rem;
    left: -0.5rem;
    opacity: 0.5;
  }
  
  &::after {
    content: '"';
    font-size: 3rem;
    color: ${props => props.theme.colors.primaryLight};
    position: absolute;
    bottom: -2rem;
    right: -0.5rem;
    opacity: 0.5;
  }
`;

const TestimonialUser = styled.div`
  display: flex;
  align-items: center;
  margin-top: 2rem;
`;

const TestimonialAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 1rem;
  background-color: ${props => props.color || props.theme.colors.primaryLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
`;

const TestimonialUserInfo = styled.div``;

const TestimonialUserName = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const TestimonialUserTitle = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textLight};
`;

const SliderControls = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  gap: 0.5rem;
`;

const SliderDot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background-color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.backgroundAlt};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    transform: scale(1.2);
  }
  
  &:focus {
    outline: none;
  }
`;

// Donation Section Styled Components
const DonationContainer = styled.div`
  margin-top: 4rem;
  position: relative;
  opacity: 0;
  animation: ${fadeIn} 0.8s ease forwards;
  animation-delay: 0.8s;
  background-color: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: ${props => props.theme.shadows.medium};
`;

const DonationTitle = styled.h3`
  font-size: 1.75rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.text};
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    margin-right: 0.75rem;
    color: ${props => props.theme.colors.primary};
  }
`;

const DonationSubtitle = styled.p`
  text-align: center;
  color: ${props => props.theme.colors.textLight};
  max-width: 800px;
  margin: 0 auto 2rem;
`;

const DonationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const DonationOptions = styled.div`
  padding: 1rem;
`;

const DonationOptionsTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.text};
`;

const DonationAmounts = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const DonationAmount = styled.button`
  padding: 1rem;
  border-radius: 8px;
  border: 2px solid ${props => props.active ? props.theme.colors.primary : props.theme.colors.backgroundAlt};
  background-color: ${props => props.active ? `${props.theme.colors.primary}10` : 'white'};
  color: ${props => props.theme.colors.text};
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => `${props.theme.colors.primary}10`};
  }
  
  .amount {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
    color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.text};
  }
  
  .label {
    font-size: 0.9rem;
    color: ${props => props.theme.colors.textLight};
  }
`;

const PaymentMethods = styled.div`
  margin-top: 2rem;
`;

const PaymentMethodsTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.text};
`;

const PaymentMethodsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PaymentMethod = styled.button`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 2px solid ${props => props.active ? props.theme.colors.primary : props.theme.colors.backgroundAlt};
  background-color: ${props => props.active ? `${props.theme.colors.primary}10` : 'white'};
  color: ${props => props.theme.colors.text};
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => `${props.theme.colors.primary}10`};
  }
  
  svg {
    margin-right: 0.75rem;
    color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.textLight};
  }
`;

const DonationDetails = styled.div`
  background-color: ${props => props.theme.colors.backgroundAlt}50;
  border-radius: 8px;
  padding: 1.5rem;
`;

const DonationDetailsTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.text};
`;

const QrCodeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const QrCodeImage = styled.img`
  max-width: 200px;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const BankDetails = styled.div`
  margin-top: 1.5rem;
`;

const BankDetailsTitle = styled.h5`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: ${props => props.theme.colors.text};
`;

const BankDetailsList = styled.dl`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 0.5rem;
  font-size: 0.95rem;
`;

const BankDetailsLabel = styled.dt`
  color: ${props => props.theme.colors.textLight};
`;

const BankDetailsValue = styled.dd`
  font-weight: 500;
  color: ${props => props.theme.colors.text};
`;

const IBAN = styled.div`
  font-family: monospace;
  background-color: white;
  padding: 0.75rem;
  border-radius: 4px;
  border: 1px solid ${props => props.theme.colors.backgroundAlt};
  margin-top: 0.5rem;
  font-size: 1.1rem;
  text-align: center;
  letter-spacing: 1px;
`;

const DonateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 1rem;
  border-radius: 8px;
  background-color: ${props => props.theme.colors.primary};
  color: white;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1.5rem;
  
  &:hover {
    background-color: ${props => props.theme.colors.primaryDark};
    transform: translateY(-2px);
  }
  
  svg {
    margin-right: 0.75rem;
  }
`;

const ThankYouMessage = styled.div`
  background-color: #e6f7eb;
  border-radius: 8px;
  padding: 1.5rem;
  text-align: center;
  margin-top: 2rem;
  
  h4 {
    font-weight: 600;
    color: #2f855a;
    margin-bottom: 0.5rem;
  }
  
  p {
    color: #276749;
  }
`;

// Community Component
const Community = () => {
  const { translate } = useLanguage();
  const [activeSlide, setActiveSlide] = useState(0);
  const [totalReports, setTotalReports] = useState(0);
  const [totalValidations, setTotalValidations] = useState(0);
  const [activeUsers, setActiveUsers] = useState(0);
  const [countriesCovered, setCountriesCovered] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showThankYou, setShowThankYou] = useState(false);
  const [selectedDonationAmount, setSelectedDonationAmount] = useState(20);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('bank_transfer');
  const [features, setFeatures] = useState([
    {
      title: translate('community.features.weatherAlerts.title', 'Weather Alerts'),
      status: translate('community.features.weatherAlerts.status', 'In Development'),
      votes: 427,
      percentage: 85,
      userVote: null,
      statusColor: '#4a9cff',
      color: '#4a9cff'
    },
    {
      title: translate('community.features.timeTravel.title', 'Historical Weather Timeline'),
      status: translate('community.features.timeTravel.status', 'Planned'),
      votes: 316,
      percentage: 63,
      userVote: null,
      statusColor: '#8a68ff',
      color: '#8a68ff'
    },
    {
      title: translate('community.features.aiPrediction.title', 'AI Weather Prediction'),
      status: translate('community.features.aiPrediction.status', 'Under Review'),
      votes: 245,
      percentage: 49,
      userVote: null,
      statusColor: '#ff8e56',
      color: '#ff8e56'
    },
    {
      title: translate('community.features.weatherGames.title', 'Weather Mini-Games'),
      status: translate('community.features.weatherGames.status', 'Suggested'),
      votes: 183,
      percentage: 37,
      userVote: null,
      statusColor: '#56c7ff',
      color: '#56c7ff'
    }
  ]);
  
  const sliderRef = useRef(null);
  
  // Handle voting for features
  const handleVote = (index, voteType) => {
    const newFeatures = [...features];
    const feature = newFeatures[index];
    
    // If already voted the same way, cancel the vote
    if (feature.userVote === voteType) {
      feature.userVote = null;
      feature.votes += (voteType === 'up' ? -1 : 1);
    } 
    // If voted the opposite way, change the vote
    else if (feature.userVote !== null) {
      feature.userVote = voteType;
      feature.votes += (voteType === 'up' ? 2 : -2);
    } 
    // If not voted before
    else {
      feature.userVote = voteType;
      feature.votes += (voteType === 'up' ? 1 : -1);
    }
    
    // Recalculate percentages based on max votes
    const maxVotes = Math.max(...newFeatures.map(f => f.votes));
    newFeatures.forEach(f => {
      f.percentage = Math.round((f.votes / maxVotes) * 100);
    });
    
    setFeatures(newFeatures);
  };
  
  // Navigate to a specific slide
  const goToSlide = (index) => {
    setActiveSlide(index);
    if (sliderRef.current) {
      const slideWidth = sliderRef.current.children[0].offsetWidth + 32; // Including gap
      sliderRef.current.scrollLeft = slideWidth * index;
    }
  };
  
  // Testimonials data using actual team members
  const testimonials = [
    {
      content: translate('community.testimonials.0.content', "The hyperlocal weather data helps me plan my outdoor activities. Being able to contribute reports makes me feel part of improving forecasts for everyone."),
      name: "Pavel",
      title: translate('community.testimonials.0.title', "Project Founder"),
      avatar: "🌦️",
      color: "rgba(74, 156, 255, 0.3)",
    },
    {
      content: translate('community.testimonials.1.content', "I appreciate the community validation system for ensuring data quality. It helps distinguish reliable reports from less accurate ones."),
      name: "Anemone",
      title: translate('community.testimonials.1.title', "Weather Enthusiast"),
      avatar: "🌈",
      color: "rgba(255, 142, 86, 0.3)",
    },
    {
      content: translate('community.testimonials.2.content', "The map interface makes it easy to find weather conditions in specific locations. I can quickly check reports before heading out."),
      name: "Radu",
      title: translate('community.testimonials.2.title', "Early Adopter"),
      avatar: "☀️",
      color: "rgba(138, 104, 255, 0.3)",
    },
    {
      content: translate('community.testimonials.3.content', "I like how the app combines traditional forecasts with real-time community reports. The emoji system makes it simple to understand current conditions at a glance."),
      name: "Camil",
      title: translate('community.testimonials.3.title', "Regular Contributor"),
      avatar: "🌧️",
      color: "rgba(86, 199, 255, 0.3)",
    }
  ];
  
  // Handle automatic testimonial sliding
  useEffect(() => {
    const interval = setInterval(() => {
      const nextSlide = (activeSlide + 1) % testimonials.length;
      goToSlide(nextSlide);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [activeSlide, testimonials.length]);
  
  // Fetch metrics from API when component mounts
  useEffect(() => {
    const fetchMetrics = async () => {
      setIsLoading(true);
      try {
        // Fetch community metrics from the API
        const metricsResponse = await statisticsApi.getMetrics();
        
        if (metricsResponse && metricsResponse.data) {
          const data = metricsResponse.data;
          
          // Update state with API data
          setTotalReports(data.totalReports || 0);
          setTotalValidations(data.totalValidations || 0);
          setActiveUsers(data.activeUsers || 0);
          setCountriesCovered(data.countriesCovered || 0);
          
          // Set up polling for real-time updates every 30 seconds
          const intervalId = setInterval(async () => {
            try {
              const refreshResponse = await statisticsApi.getMetrics(false); // Don't use cache
              if (refreshResponse && refreshResponse.data) {
                setTotalReports(refreshResponse.data.totalReports || 0);
                setTotalValidations(refreshResponse.data.totalValidations || 0);
                setActiveUsers(refreshResponse.data.activeUsers || 0);
                setCountriesCovered(refreshResponse.data.countriesCovered || 0);
              }
            } catch (error) {
              console.error('Error refreshing community metrics:', error);
            }
          }, 30000);
          
          // Clean up interval on component unmount
          return () => clearInterval(intervalId);
        }
      } catch (error) {
        console.error('Error fetching community metrics from API:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchMetrics();
  }, []);
  
  // Fetch top contributors from API
  useEffect(() => {
    const fetchTopContributors = async () => {
      try {
        const response = await statisticsApi.getTopContributors(4);
        if (response && response.contributors && response.contributors.length > 0) {
          // Update testimonials with contributor data if available
          const updatedTestimonials = [...testimonials];
          response.contributors.forEach((contributor, index) => {
            if (index < updatedTestimonials.length) {
              // Only update name if available and not empty
              if (contributor.name && contributor.name !== 'Anonymous User') {
                updatedTestimonials[index].name = contributor.name;
              }
            }
          });
          // Don't update the state if no valid contributors found
          if (updatedTestimonials.some(t => t.name !== testimonials[0].name)) {
            setTestimonials(updatedTestimonials);
          }
        }
      } catch (error) {
        console.error('Error fetching top contributors:', error);
      }
    };
    
    fetchTopContributors();
  }, []);
  
  // Format numbers for display
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M+';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K+';
    } else if (num > 0) {
      return num + '+';
    }
    return '0';
  };

  return (
    <CommunitySection id="community">
      <SectionInner>
        <SectionHeader>
          <SectionTitle>{translate('community.title', 'Community Engagement')}</SectionTitle>
          <SectionSubtitle>
            {translate('community.subtitle', 'Join a global network of weather enthusiasts who contribute reports, validate data, and shape the future of WeatherTeller through active participation.')}
          </SectionSubtitle>
        </SectionHeader>
        
        <StatsContainer>
          <StatCard delay="0.1s">
            <StatIcon color="#4a9cff">🌐</StatIcon>
            <StatNumber delay="0.1s">
              {isLoading ? '...' : formatNumber(activeUsers)}
            </StatNumber>
            <StatLabel>{translate('community.stats.users', 'Active Users')}</StatLabel>
          </StatCard>
          
          <StatCard delay="0.2s">
            <StatIcon color="#ff8e56">📊</StatIcon>
            <StatNumber delay="0.2s">
              {isLoading ? '...' : formatNumber(totalReports)}
            </StatNumber>
            <StatLabel>{translate('community.stats.reports', 'Weather Reports')}</StatLabel>
          </StatCard>
          
          <StatCard delay="0.3s">
            <StatIcon color="#8a68ff">🔍</StatIcon>
            <StatNumber delay="0.3s">
              {isLoading ? '...' : formatNumber(totalValidations)}
            </StatNumber>
            <StatLabel>{translate('community.stats.validations', 'Report Validations')}</StatLabel>
          </StatCard>
          
          <StatCard delay="0.4s">
            <StatIcon color="#56c7ff">🌍</StatIcon>
            <StatNumber delay="0.4s">
              {isLoading ? '...' : formatNumber(countriesCovered)}
            </StatNumber>
            <StatLabel>{translate('community.stats.countries', 'Countries Covered')}</StatLabel>
          </StatCard>
        </StatsContainer>
        
        <CommunityGrid>
          <CommunityColumn delay="0.2s">
            <ContributorsContainer>
              <ContributorsTitle>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8.5 11C10.7091 11 12.5 9.20914 12.5 7C12.5 4.79086 10.7091 3 8.5 3C6.29086 3 4.5 4.79086 4.5 7C4.5 9.20914 6.29086 11 8.5 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20 8V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M23 11H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {translate('community.contributors.title', 'Top Contributors')}
              </ContributorsTitle>
              
              <ContributorsList>
                <ContributorCard>
                  <ContributorAvatar>🌦️</ContributorAvatar>
                  <ContributorInfo>
                    <ContributorName>Pavel</ContributorName>
                    <ContributorStats>
                      <span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        42 {translate('community.contributors.reports', 'reports')}
                      </span>
                      <span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14 9V5C14 4.46957 13.7893 3.96086 13.4142 3.58579C13.0391 3.21071 12.5304 3 12 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H12C12.5304 21 13.0391 20.7893 13.4142 20.4142C13.7893 20.0391 14 19.5304 14 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M7 15L21 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M18 12L21 15L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        98% {translate('community.contributors.accurate', 'accurate')}
                      </span>
                    </ContributorStats>
                  </ContributorInfo>
                  <ContributorBadge color="rgba(74, 156, 255, 0.2)" $textColor="#4a9cff">
                    {translate('community.contributors.badge.founder', 'Founder')}
                  </ContributorBadge>
                </ContributorCard>
                
                <ContributorCard>
                  <ContributorAvatar>🌈</ContributorAvatar>
                  <ContributorInfo>
                    <ContributorName>Anemone</ContributorName>
                    <ContributorStats>
                      <span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        36 {translate('community.contributors.reports', 'reports')}
                      </span>
                      <span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14 9V5C14 4.46957 13.7893 3.96086 13.4142 3.58579C13.0391 3.21071 12.5304 3 12 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H12C12.5304 21 13.0391 20.7893 13.4142 20.4142C13.7893 20.0391 14 19.5304 14 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M7 15L21 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M18 12L21 15L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        96% {translate('community.contributors.accurate', 'accurate')}
                      </span>
                    </ContributorStats>
                  </ContributorInfo>
                  <ContributorBadge color="rgba(255, 142, 86, 0.2)" $textColor="#ff8e56">
                    {translate('community.contributors.badge.contributor', 'Contributor')}
                  </ContributorBadge>
                </ContributorCard>
                
                <ContributorCard>
                  <ContributorAvatar>☀️</ContributorAvatar>
                  <ContributorInfo>
                    <ContributorName>Radu</ContributorName>
                    <ContributorStats>
                      <span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        29 {translate('community.contributors.reports', 'reports')}
                      </span>
                      <span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14 9V5C14 4.46957 13.7893 3.96086 13.4142 3.58579C13.0391 3.21071 12.5304 3 12 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H12C12.5304 21 13.0391 20.7893 13.4142 20.4142C13.7893 20.0391 14 19.5304 14 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M7 15L21 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M18 12L21 15L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        95% {translate('community.contributors.accurate', 'accurate')}
                      </span>
                    </ContributorStats>
                  </ContributorInfo>
                  <ContributorBadge color="rgba(138, 104, 255, 0.2)" $textColor="#8a68ff">
                    {translate('community.contributors.badge.contributor', 'Contributor')}
                  </ContributorBadge>
                </ContributorCard>
                
                <ContributorCard>
                  <ContributorAvatar>🌧️</ContributorAvatar>
                  <ContributorInfo>
                    <ContributorName>Camil</ContributorName>
                    <ContributorStats>
                      <span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        23 {translate('community.contributors.reports', 'reports')}
                      </span>
                      <span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14 9V5C14 4.46957 13.7893 3.96086 13.4142 3.58579C13.0391 3.21071 12.5304 3 12 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H12C12.5304 21 13.0391 20.7893 13.4142 20.4142C13.7893 20.0391 14 19.5304 14 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M7 15L21 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M18 12L21 15L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        92% {translate('community.contributors.accurate', 'accurate')}
                      </span>
                    </ContributorStats>
                  </ContributorInfo>
                  <ContributorBadge color="rgba(86, 199, 255, 0.2)" $textColor="#56c7ff">
                    {translate('community.contributors.badge.contributor', 'Contributor')}
                  </ContributorBadge>
                </ContributorCard>
              </ContributorsList>
            </ContributorsContainer>
          </CommunityColumn>
          
          <CommunityColumn delay="0.4s">
            <VotingContainer>
              <VotingTitle>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 8.00006C16.5304 8.00006 17.0391 7.78934 17.4142 7.41427C17.7893 7.0392 18 6.53049 18 6.00006C18 5.46963 17.7893 4.96092 17.4142 4.58585C17.0391 4.21078 16.5304 4.00006 16 4.00006C15.4696 4.00006 14.9609 4.21078 14.5858 4.58585C14.2107 4.96092 14 5.46963 14 6.00006C14 6.53049 14.2107 7.0392 14.5858 7.41427C14.9609 7.78934 15.4696 8.00006 16 8.00006Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 14.0001H11C11.5304 14.0001 12.0391 13.7893 12.4142 13.4143C12.7893 13.0392 13 12.5305 13 12.0001V4.00006H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 20.0001L14 20.0001" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M11 20.0001V14.0001" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20 12.0001H18C17.4696 12.0001 16.9609 12.2108 16.5858 12.5858C16.2107 12.9609 16 13.4696 16 14.0001V20.0001H22V14.0001C22 13.4696 21.7893 12.9609 21.4142 12.5858C21.0391 12.2108 20.5304 12.0001 20 12.0001Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {translate('community.voting.title', 'Feature Voting')}
              </VotingTitle>
              
              <FeaturesList>
                {features.map((feature, index) => (
                  <FeatureItem key={index}>
                    <FeatureHeader>
                      <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureStatus color={`${feature.statusColor}20`} $textColor={feature.statusColor}>
                        {feature.status}
                      </FeatureStatus>
                    </FeatureHeader>
                    
                    <ProgressBar>
                      <ProgressFill percentage={feature.percentage} color={feature.color} />
                    </ProgressBar>
                    
                    <VoteCount>
                      <span>{feature.votes} {translate('community.voting.votes', 'votes')}</span>
                      <VoteButtons>
                        <VoteButton 
                          color="#4a9cff" 
                          active={feature.userVote === 'up'}
                          onClick={() => handleVote(index, 'up')}
                          aria-label={translate('community.voting.upvote', 'Upvote')}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 19V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M5 12L12 5L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </VoteButton>
                        
                        <VoteButton 
                          color="#ff6b6b" 
                          active={feature.userVote === 'down'}
                          onClick={() => handleVote(index, 'down')}
                          aria-label={translate('community.voting.downvote', 'Downvote')}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M19 12L12 19L5 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </VoteButton>
                      </VoteButtons>
                    </VoteCount>
                  </FeatureItem>
                ))}
              </FeaturesList>
            </VotingContainer>
          </CommunityColumn>
        </CommunityGrid>
        
        <TestimonialsContainer>
          <TestimonialsTitle>{translate('community.testimonials.title', 'User Testimonials')}</TestimonialsTitle>
          
          <TestimonialsSlider ref={sliderRef}>
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index}>
                <TestimonialContent>
                  {testimonial.content}
                </TestimonialContent>
                <TestimonialUser>
                  <TestimonialAvatar color={testimonial.color}>
                    {testimonial.avatar}
                  </TestimonialAvatar>
                  <TestimonialUserInfo>
                    <TestimonialUserName>{testimonial.name}</TestimonialUserName>
                    <TestimonialUserTitle>{testimonial.title}</TestimonialUserTitle>
                  </TestimonialUserInfo>
                </TestimonialUser>
              </TestimonialCard>
            ))}
          </TestimonialsSlider>
          
          <SliderControls>
            {testimonials.map((_, index) => (
              <SliderDot 
                key={index} 
                active={activeSlide === index}
                onClick={() => goToSlide(index)}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </SliderControls>
        </TestimonialsContainer>
        
        {/* Donation Section */}
        <DonationContainer id="donate">
          <DonationTitle>
            <Heart size={24} />
            {translate('donation.title', 'Support WeatherTeller')}
          </DonationTitle>
          
          <DonationSubtitle>
            {translate('donation.subtitle', 'Help us improve weather data for everyone by supporting the development of new features and infrastructure')}
          </DonationSubtitle>
          
          {showThankYou ? (
            <ThankYouMessage>
              <h4>{translate('donation.thank_you.title', 'Thank You for Your Support!')}</h4>
              <p>{translate('donation.thank_you.message', 'Your contribution helps us continue developing better weather data tools for everyone')}</p>
              
              {(selectedPaymentMethod === 'bank_transfer' || selectedPaymentMethod === 'qr_bill') && (
                <BankDetails>
                  <BankDetailsTitle>{translate('donation.bank_details.title', 'Please use the following details to complete your donation:')}</BankDetailsTitle>
                  <BankDetailsList>
                    <BankDetailsLabel>{translate('donation.bank_details.account_holder', 'Account Holder:')}</BankDetailsLabel>
                    <BankDetailsValue>Pavel Maxim</BankDetailsValue>
                    
                    <BankDetailsLabel>{translate('donation.bank_details.iban', 'IBAN:')}</BankDetailsLabel>
                    <BankDetailsValue>
                      <IBAN>CH93 0027 6276 1062 8840 U</IBAN>
                    </BankDetailsValue>
                    
                    <BankDetailsLabel>{translate('donation.bank_details.amount', 'Amount:')}</BankDetailsLabel>
                    <BankDetailsValue>{selectedDonationAmount} CHF</BankDetailsValue>
                    
                    <BankDetailsLabel>{translate('donation.bank_details.reference', 'Reference:')}</BankDetailsLabel>
                    <BankDetailsValue>WeatherTeller Support</BankDetailsValue>
                  </BankDetailsList>
                </BankDetails>
              )}
              
              <DonateButton onClick={() => setShowThankYou(false)}>
                {translate('donation.return', 'Return to Donation Options')}
              </DonateButton>
            </ThankYouMessage>
          ) : (
            <DonationGrid>
              <DonationOptions>
                <DonationOptionsTitle>
                  {translate('donation.options.title', 'Choose Your Contribution')}
                </DonationOptionsTitle>
                
                <DonationAmounts>
                  <DonationAmount 
                    active={selectedDonationAmount === 10}
                    onClick={() => setSelectedDonationAmount(10)}
                  >
                    <div className="amount">10 CHF</div>
                    <div className="label">{translate('donation.amount.10chf', 'Basic Support')}</div>
                  </DonationAmount>
                  
                  <DonationAmount 
                    active={selectedDonationAmount === 20}
                    onClick={() => setSelectedDonationAmount(20)}
                  >
                    <div className="amount">20 CHF</div>
                    <div className="label">{translate('donation.amount.20chf', 'Standard Support')}</div>
                  </DonationAmount>
                  
                  <DonationAmount 
                    active={selectedDonationAmount === 50}
                    onClick={() => setSelectedDonationAmount(50)}
                  >
                    <div className="amount">50 CHF</div>
                    <div className="label">{translate('donation.amount.50chf', 'Premium Support')}</div>
                  </DonationAmount>
                  
                  <DonationAmount 
                    active={selectedDonationAmount === 100}
                    onClick={() => setSelectedDonationAmount(100)}
                  >
                    <div className="amount">100 CHF</div>
                    <div className="label">{translate('donation.amount.100chf', 'Gold Supporter')}</div>
                  </DonationAmount>
                </DonationAmounts>
                
                <PaymentMethods>
                  <PaymentMethodsTitle>
                    {translate('donation.payment_methods.title', 'Select Payment Method')}
                  </PaymentMethodsTitle>
                  
                  <PaymentMethodsList>
                    <PaymentMethod 
                      active={selectedPaymentMethod === 'bank_transfer'}
                      onClick={() => setSelectedPaymentMethod('bank_transfer')}
                    >
                      <Landmark size={20} />
                      {translate('donation.payment_methods.bank_transfer', 'Bank Transfer (preferred)')}
                    </PaymentMethod>
                    
                    <PaymentMethod 
                      active={selectedPaymentMethod === 'qr_bill'}
                      onClick={() => setSelectedPaymentMethod('qr_bill')}
                    >
                      <QrCode size={20} />
                      {translate('donation.payment_methods.qr_bill', 'Swiss QR Bill')}
                    </PaymentMethod>
                    
                    <PaymentMethod 
                      active={selectedPaymentMethod === 'card'}
                      onClick={() => setSelectedPaymentMethod('card')}
                    >
                      <CreditCard size={20} />
                      {translate('donation.payment_methods.card', 'Credit Card')}
                    </PaymentMethod>
                  </PaymentMethodsList>
                </PaymentMethods>
                
                <DonateButton onClick={() => setShowThankYou(true)}>
                  <DollarSign size={20} />
                  {translate('donation.button', 'Donate Now')}
                </DonateButton>
              </DonationOptions>
              
              <DonationDetails>
                <DonationDetailsTitle>
                  {translate('donation.why.title', 'Why Support WeatherTeller?')}
                </DonationDetailsTitle>
                
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Heart className="text-red-500 h-5 w-5 mt-0.5 mr-2 flex-shrink-0" />
                    <p className="text-gray-600">{translate('donation.why.reason1', 'Help build better weather data collection and analysis tools')}</p>
                  </li>
                  <li className="flex items-start">
                    <DollarSign className="text-green-500 h-5 w-5 mt-0.5 mr-2 flex-shrink-0" />
                    <p className="text-gray-600">{translate('donation.why.reason2', 'Support ongoing server costs and infrastructure')}</p>
                  </li>
                  <li className="flex items-start">
                    <CreditCard className="text-blue-500 h-5 w-5 mt-0.5 mr-2 flex-shrink-0" />
                    <p className="text-gray-600">{translate('donation.why.reason3', 'Enable development of new features and capabilities')}</p>
                  </li>
                </ul>
                
                {selectedPaymentMethod === 'qr_bill' && (
                  <QrCodeContainer>
                    <p className="text-center text-gray-600 mb-2">
                      {translate('donation.qr.scan', 'Scan the QR code to donate:')}
                    </p>
                    <QrCodeImage 
                      src="/qr2donation.jpg" 
                      alt="QR Bill" 
                    />
                  </QrCodeContainer>
                )}
                
                {(selectedPaymentMethod === 'bank_transfer' || selectedPaymentMethod === 'qr_bill') && (
                  <BankDetails>
                    <BankDetailsTitle>{translate('donation.bank_details.title', 'Bank Transfer Details')}</BankDetailsTitle>
                    <BankDetailsList>
                      <BankDetailsLabel>{translate('donation.bank_details.account_holder', 'Account Holder:')}</BankDetailsLabel>
                      <BankDetailsValue>Pavel Maxim</BankDetailsValue>
                      
                      <BankDetailsLabel>{translate('donation.bank_details.iban', 'IBAN:')}</BankDetailsLabel>
                      <BankDetailsValue>
                        <IBAN>CH93 0027 6276 1062 8840 U</IBAN>
                      </BankDetailsValue>
                      
                      <BankDetailsLabel>{translate('donation.bank_details.reference', 'Reference:')}</BankDetailsLabel>
                      <BankDetailsValue>WeatherTeller Support</BankDetailsValue>
                    </BankDetailsList>
                  </BankDetails>
                )}
              </DonationDetails>
            </DonationGrid>
          )}
        </DonationContainer>
      </SectionInner>
    </CommunitySection>
  );
};

export default Community;